import React, { useState } from 'react';
import { SendIcon, XIcon, CalendarIcon, MapPinIcon, LayoutGridIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { BrandRequest } from '../../data/types';
interface ReviewInvitationModalProps {
  isOpen: boolean;
  request: BrandRequest | null;
  onClose: () => void;
  onRespond: (requestId: string, response: 'accepted' | 'rejected', message?: string) => void;
}
export function ReviewInvitationModal({
  isOpen,
  request,
  onClose,
  onRespond
}: ReviewInvitationModalProps) {
  const [message, setMessage] = useState('');
  if (!isOpen || !request) return null;
  const handleClose = () => {
    setMessage('');
    onClose();
  };
  const handleAccept = () => {
    onRespond(request.id, 'accepted', message);
    handleClose();
  };
  const handleDecline = () => {
    onRespond(request.id, 'rejected', message);
    handleClose();
  };
  const formatDates = (start: string, end: string) => {
    const opts: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    };
    return `${new Date(start).toLocaleDateString('en-ZA', opts)} – ${new Date(end).toLocaleDateString('en-ZA', opts)}`;
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in" data-id="element-465">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden animate-slide-up" data-id="element-466">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border" data-id="element-467">
          <div className="flex items-center gap-3" data-id="element-468">
            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center" data-id="element-469">
              <SendIcon className="w-5 h-5 text-blue-600" data-id="element-470" />
            </div>
            <div data-id="element-471">
              <h2 className="text-lg font-bold text-text-primary" data-id="element-472">
                Review Invitation
              </h2>
              <p className="text-xs text-text-secondary" data-id="element-473">
                From Woolworths Media Team
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="text-text-tertiary hover:text-text-primary transition-colors" data-id="element-474">
            <XIcon className="w-5 h-5" data-id="element-475" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6" data-id="element-476">
          <div data-id="element-477">
            <h3 className="text-sm font-semibold text-text-primary mb-1" data-id="element-478">
              {request.campaignName || 'Media Booking Invitation'}
            </h3>
            <p className="text-sm text-text-secondary" data-id="element-479">
              Woolworths has invited you to book media space for the upcoming
              period.
            </p>
          </div>

          <div className="bg-surface-warm border border-border rounded-lg p-4 space-y-4" data-id="element-480">
            <div className="flex items-start gap-3" data-id="element-481">
              <CalendarIcon className="w-4 h-4 text-text-tertiary mt-0.5" data-id="element-482" />
              <div data-id="element-483">
                <div className="text-sm font-medium text-text-primary" data-id="element-484">
                  Week {request.weekNumber}
                </div>
                <div className="text-xs text-text-secondary" data-id="element-485">
                  {formatDates(request.weekStartDate, request.weekEndDate)}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3" data-id="element-486">
              <MapPinIcon className="w-4 h-4 text-text-tertiary mt-0.5" data-id="element-487" />
              <div data-id="element-488">
                <div className="text-sm font-medium text-text-primary" data-id="element-489">
                  {request.stores.length} Store
                  {request.stores.length !== 1 ? 's' : ''}
                </div>
                <div className="text-xs text-text-secondary" data-id="element-490">
                  {request.stores.map(s => s.storeName).join(', ')}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3" data-id="element-491">
              <LayoutGridIcon className="w-4 h-4 text-text-tertiary mt-0.5" data-id="element-492" />
              <div data-id="element-493">
                <div className="text-sm font-medium text-text-primary" data-id="element-494">
                  Media Types
                </div>
                <div className="text-xs text-text-secondary" data-id="element-495">
                  {(request.mediaTypes || [request.mediaType]).join(', ')}
                </div>
              </div>
            </div>
          </div>

          <div data-id="element-496">
            <label className="block text-sm font-medium text-text-primary mb-2" data-id="element-497">
              Add a message (optional)
            </label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Any notes for the Woolworths team..." className="w-full h-24 px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary resize-none" data-id="element-498" />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-surface-warm border-t border-border" data-id="element-499">
          <Button variant="secondary" onClick={handleDecline} className="text-urgent hover:bg-urgent/5 hover:border-urgent" data-id="element-500">
            Decline
          </Button>
          <Button variant="primary" onClick={handleAccept} data-id="element-501">
            Accept Invitation
          </Button>
        </div>
      </div>
    </div>;
}